/**
 * Wishlist module — localStorage-backed bookmarking.
 *
 * Storage shape (JSON in localStorage under STORAGE_KEY):
 *   {
 *     v: 1,
 *     items: [
 *       { handle, productId, addedAt }
 *     ]
 *   }
 *
 * Public API: window.AccelerexWishlist
 *   .has(handle)              -> boolean
 *   .toggle(handle, productId) -> 'added' | 'removed'
 *   .add(handle, productId)    -> boolean (true if added)
 *   .remove(handle)            -> boolean (true if removed)
 *   .track(action, handle, productId, details) -> void
 *   .all()                                -> array of items
 *   .count()                              -> number
 *   .clear()                              -> void
 *
 * Events (on window):
 *   'wishlist:change' — detail: { items, count, action, handle, variantId }
 */
(function () {
  "use strict";

  var STORAGE_KEY = "accelerex_wishlist";
  var SESSION_KEY = "wl_session_id";
  var BACKFILL_KEY = "accelerex_wishlist_backfill_v1";
  var TRACK_URL = "https://wishlist-admin-one.vercel.app/api/wishlist";
  var VERSION = 1;
  var MAX_ITEMS = 200;

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { v: VERSION, items: [] };
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items))
        return { v: VERSION, items: [] };
      return parsed;
    } catch (e) {
      return { v: VERSION, items: [] };
    }
  }

  function write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (e) {
      return false;
    }
  }

  function indexOf(items, handle) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === handle) return i;
    }
    return -1;
  }

  function dispatch(action, handle, variantId) {
    var state = read();
    window.dispatchEvent(
      new CustomEvent("wishlist:change", {
        detail: {
          items: state.items.slice(),
          count: state.items.length,
          action: action || null,
          handle: handle || null,
          variantId: variantId == null ? null : String(variantId),
        },
      }),
    );
  }

  function getSessionId() {
    var id = localStorage.getItem(SESSION_KEY);
    if (id) return id;

    if (window.crypto && window.crypto.randomUUID) {
      id = window.crypto.randomUUID();
    } else {
      id = String(Date.now()) + "-" + Math.random().toString(16).slice(2);
    }

    localStorage.setItem(SESSION_KEY, id);
    return id;
  }

  function nullableString(value) {
    if (value == null || value === "") return null;
    return String(value);
  }

  function normalizeTimestamp(value) {
    if (value == null || value === "") return null;
    var date = /^\d+$/.test(String(value))
      ? new Date(Number(value))
      : new Date(String(value));
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  function track(action, handle, productId, details) {
    if (!TRACK_URL || !window.fetch) return Promise.resolve(false);
    if (action !== "add" && action !== "remove") return Promise.resolve(false);

    details = details || {};

    var payload = {
      session_id: getSessionId(),
      customer_id: nullableString(details.customerId),
      product_id: nullableString(productId || details.productId || handle),
      product_title: nullableString(details.productTitle),
      product_handle: nullableString(handle || details.productHandle),
      variant_id: nullableString(details.variantId),
      variant_title: nullableString(details.variantTitle),
      variant_color: nullableString(details.variantColor),
      image_url: nullableString(details.imageUrl),
      price: nullableString(details.price),
      action: action,
    };

    var addedAt = normalizeTimestamp(details.addedAt);
    if (addedAt) payload.added_at = addedAt;

    return window
      .fetch(TRACK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
      .then(function (response) {
        if (!response.ok) throw new Error("Wishlist tracking failed: " + response.status);
        return true;
      })
      .catch(function (error) {
        console.warn("Wishlist tracking failed", error);
        return false;
      });
  }

  function getTrackingDetails(btn) {
    return {
      customerId: btn.getAttribute("data-wishlist-customer-id"),
      productTitle: btn.getAttribute("data-wishlist-product-title"),
      productHandle: btn.getAttribute("data-wishlist-handle"),
      variantId: btn.getAttribute("data-wishlist-variant-id"),
      variantTitle: btn.getAttribute("data-wishlist-variant-title"),
      variantColor: btn.getAttribute("data-wishlist-variant-color"),
      imageUrl: btn.getAttribute("data-wishlist-image-url"),
      price: btn.getAttribute("data-wishlist-price"),
    };
  }

  function getTrackingDetailsForHandle(handle) {
    var btns = document.querySelectorAll("[data-wishlist-toggle]");
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].getAttribute("data-wishlist-handle") === handle) {
        return getTrackingDetails(btns[i]);
      }
    }
    return {};
  }

  function backfillExistingItems() {
    try {
      if (localStorage.getItem(BACKFILL_KEY) === "1") return;

      var items = read().items || [];
      if (!items.length) {
        localStorage.setItem(BACKFILL_KEY, "1");
        return;
      }

      var requests = [];
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item || !item.handle) continue;

        var details = getTrackingDetailsForHandle(item.handle);
        details.addedAt = item.addedAt || null;
        requests.push(track("add", item.handle, item.productId, details));
      }

      if (!requests.length) {
        localStorage.setItem(BACKFILL_KEY, "1");
        return;
      }

      Promise.all(requests).then(function (results) {
        var allOk = results.every(Boolean);
        if (allOk) localStorage.setItem(BACKFILL_KEY, "1");
      });
    } catch (error) {
      console.warn("Wishlist backfill failed", error);
    }
  }

  var Wishlist = {
    has: function (handle) {
      if (!handle) return false;
      return indexOf(read().items, handle) !== -1;
    },

    add: function (handle, productId) {
      if (!handle) return false;
      var state = read();
      if (indexOf(state.items, handle) !== -1) return false;
      if (state.items.length >= MAX_ITEMS) state.items.shift();
      state.items.push({
        handle: handle,
        productId: productId == null ? null : String(productId),
        addedAt: Date.now(),
      });
      write(state);
      dispatch("added", handle, null);
      return true;
    },

    remove: function (handle) {
      if (!handle) return false;
      var state = read();
      var idx = indexOf(state.items, handle);
      if (idx === -1) return false;
      state.items.splice(idx, 1);
      write(state);
      dispatch("removed", handle, null);
      return true;
    },

    track: function (action, handle, productId, details) {
      track(action, handle, productId, details);
    },

    toggle: function (handle, productId) {
      if (this.has(handle)) {
        this.remove(handle);
        return "removed";
      }
      this.add(handle, productId);
      return "added";
    },

    all: function () {
      return read().items.slice();
    },

    count: function () {
      return read().items.length;
    },

    clear: function () {
      write({ v: VERSION, items: [] });
      dispatch("cleared");
    },
  };

  window.AccelerexWishlist = Wishlist;

  // ---------------------------------------------------------------
  // Toggle button binding — any element with [data-wishlist-toggle]
  //   data-wishlist-handle="<product-handle>" (required)
  //   data-wishlist-product-id="<id>"
  // Adds .is-active when the product handle is in the list.
  // ---------------------------------------------------------------
  function syncButton(btn) {
    var handle = btn.getAttribute("data-wishlist-handle");
    if (!handle) return;
    var active = Wishlist.has(handle);
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    var labelAdd = btn.getAttribute("data-label-add");
    var labelRemove = btn.getAttribute("data-label-remove");
    if (labelAdd && labelRemove) {
      btn.setAttribute("aria-label", active ? labelRemove : labelAdd);
      var srEl = btn.querySelector("[data-wishlist-sr]");
      if (srEl) srEl.textContent = active ? labelRemove : labelAdd;
    }
  }

  function syncAllButtons() {
    var btns = document.querySelectorAll("[data-wishlist-toggle]");
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
  }

  function syncCounters() {
    var count = Wishlist.count();
    var els = document.querySelectorAll("[data-wishlist-count]");
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(count);
      els[i].toggleAttribute("hidden", count === 0);
      els[i].classList.toggle("is-empty", count === 0);
    }
    var hasItems = count > 0;
    var roots = document.querySelectorAll("[data-wishlist-has-items]");
    for (var j = 0; j < roots.length; j++) {
      roots[j].toggleAttribute("hidden", !hasItems);
    }
    var emptyRoots = document.querySelectorAll("[data-wishlist-empty]");
    for (var k = 0; k < emptyRoots.length; k++) {
      emptyRoots[k].toggleAttribute("hidden", hasItems);
    }
  }

  function onClick(e) {
    var btn = e.target.closest && e.target.closest("[data-wishlist-toggle]");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute("data-wishlist-handle");
    if (!handle) return;

    var productId = btn.getAttribute("data-wishlist-product-id") || null;

    var action = Wishlist.toggle(handle, productId);
    var active = action === "added";
    track(active ? "add" : "remove", handle, productId, getTrackingDetails(btn));

    // Update this button immediately
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");

    var labelAdd = btn.getAttribute("data-label-add");
    var labelRemove = btn.getAttribute("data-label-remove");

    if (labelAdd && labelRemove) {
      btn.setAttribute("aria-label", active ? labelRemove : labelAdd);

      var srEl = btn.querySelector("[data-wishlist-sr]");
      if (srEl) srEl.textContent = active ? labelRemove : labelAdd;
    }

    // Then sync everything else
    syncCounters();
    syncAllButtons();
  }

  function onChange() {
    syncAllButtons();
    syncCounters();
  }

  function init() {
    document.addEventListener("click", onClick, true);
    window.addEventListener("wishlist:change", onChange);
    // Cross-tab sync
    window.addEventListener("storage", function (e) {
      if (e.key === STORAGE_KEY) onChange();
    });
    onChange();
    backfillExistingItems();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-sync after Shopify section reloads in the customizer
  document.addEventListener("shopify:section:load", onChange);
  // Re-sync after our own re-renders (e.g. wishlist page item removed)
  document.addEventListener("wishlist:rebound", onChange);

  // Variant choice belongs on the wishlist page, so PDP variant changes do not
  // change wishlist storage or button state.
})();
